import React, { useState, useMemo, useEffect, useRef } from 'react'
import { useGetEstimates } from '../../api/estimates/hooks/useGetEstimates'
import { EstimateFilters } from '../../features/EstimateFilters'
import { EstimateSummaryCards } from '../../features/EstimateSummaryCards'
import { EstimateTable } from '../../features/EstimateTable'
import { Button } from '../../shared/ui/Button'
import {
  PageContainer,
  PageHeader,
  HeaderContent,
  PageTitle,
  PageSubtitle,
  HeaderActions,
  ContentContainer,
  HeaderTitle,
  CenteredLoaderContainer
} from './EstimatePage.styled'
import {
  LoaderSpinner,
  LoaderText
} from '../../widgets/DocumentationWidget/DocumentationWidget.styles'
import { useParams, useLocation } from 'react-router-dom'
import { Icon } from '../../shared/ui/Icon'
import qs from 'query-string'

export const EstimatePage: React.FC = () => {
  const { livingComplexId } = useParams<{ livingComplexId: string }>()
  const location = useLocation()
  const isInitialMount = useRef(true)

  // Получаем фильтры из query-params

  // TODO: использовать useQueryParams из общик хуков
  const queryFilters = useMemo(() => {
    const query = qs.parse(location.search)
    const getParam = (key: string): string => {
      const value = query[key]
      if (Array.isArray(value)) {
        return value[0] || ''
      }
      return value?.toString() || ''
    }

    return {
      contractor: getParam('contractor'),
      oipd: getParam('oipd'),
      article: getParam('budget-item'),
      contractNumber: getParam('contractNumber'),
      actNumber: getParam('actNumber')
    }
  }, [location.search])

  // Локальный state для фильтров (обновляется сразу для UI)
  const [contractor, setContractor] = useState(() => queryFilters.contractor)
  const [oipd, setOipd] = useState(() => queryFilters.oipd)
  const [article, setArticle] = useState(() => queryFilters.article)
  const [contractNumber, setContractNumber] = useState(
    () => queryFilters.contractNumber
  )
  const [actNumber, setActNumber] = useState(() => queryFilters.actNumber)

  // TODO: переписать на нормальный debounce без дублирования
  // Debounced версии фильтров для запросов
  const [debouncedContractor, setDebouncedContractor] = useState(
    () => queryFilters.contractor
  )
  const [debouncedOipd, setDebouncedOipd] = useState(() => queryFilters.oipd)
  const [debouncedArticle, setDebouncedArticle] = useState(
    () => queryFilters.article
  )
  const [debouncedContractNumber, setDebouncedContractNumber] = useState(
    () => queryFilters.contractNumber
  )
  const [debouncedActNumber, setDebouncedActNumber] = useState(
    () => queryFilters.actNumber
  )

  // Debounce для фильтров
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedContractor(contractor)
    }, 400)
    return () => clearTimeout(timeoutId)
  }, [contractor])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedOipd(oipd)
    }, 400)
    return () => clearTimeout(timeoutId)
  }, [oipd])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedArticle(article)
    }, 400)
    return () => clearTimeout(timeoutId)
  }, [article])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedContractNumber(contractNumber)
    }, 400)
    return () => clearTimeout(timeoutId)
  }, [contractNumber])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedActNumber(actNumber)
    }, 400)
    return () => clearTimeout(timeoutId)
  }, [actNumber])

  // Синхронизация фильтров с query-params при изменении location.search (но не при первом рендере)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Обновляем state из query params
    setContractor(queryFilters.contractor)
    setOipd(queryFilters.oipd)
    setArticle(queryFilters.article)
    setContractNumber(queryFilters.contractNumber)
    setActNumber(queryFilters.actNumber)

    // Сразу обновляем debounced версии при изменении query params
    setDebouncedContractor(queryFilters.contractor)
    setDebouncedOipd(queryFilters.oipd)
    setDebouncedArticle(queryFilters.article)
    setDebouncedContractNumber(queryFilters.contractNumber)
    setDebouncedActNumber(queryFilters.actNumber)
  }, [location.search, queryFilters])

  const request = useMemo(
    () => ({
      livingComplexId: livingComplexId ? parseInt(livingComplexId) : undefined,
      contractor: debouncedContractor || undefined,
      oipd: debouncedOipd || undefined,
      costItem: debouncedArticle || undefined,
      contractNumber: debouncedContractNumber || undefined,
      actNumber: debouncedActNumber || undefined
    }),
    [
      livingComplexId,
      debouncedContractor,
      debouncedOipd,
      debouncedArticle,
      debouncedContractNumber,
      debouncedActNumber
    ]
  )

  const { data: contractorsData, isFetching: isFetchingContractors } =
    useGetEstimates(request)
  const statisticsData = contractorsData?.totals

  if (!contractorsData) {
    return (
      <PageContainer>
        <CenteredLoaderContainer>
          <LoaderSpinner />
          <LoaderText>Загрузка данных...</LoaderText>
        </CenteredLoaderContainer>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader>
        <HeaderContent>
          <Icon size={21} type="estimates" className="bg-gray" />
          <HeaderTitle>
            <PageTitle>Сметы</PageTitle>
            <PageSubtitle>
              Управление сметами, договорами, актами и оплатами
            </PageSubtitle>
          </HeaderTitle>
        </HeaderContent>
        <HeaderActions>
          <Button>
            <Icon type="calculate" size={14} />
            Создать заявку (ЦГП)
          </Button>
          <Button variant="secondary" size="medium">
            <Icon type="plus" size={14} />
            Создать заявку (не-ЦГП)
          </Button>
        </HeaderActions>
      </PageHeader>

      <ContentContainer>
        <EstimateFilters
          contractor={contractor}
          oipd={oipd}
          article={article}
          contractNumber={contractNumber}
          actNumber={actNumber}
          onContractorChange={setContractor}
          onOipdChange={setOipd}
          onArticleChange={setArticle}
          onContractNumberChange={setContractNumber}
          onActNumberChange={setActNumber}
        />

        {statisticsData && <EstimateSummaryCards statistics={statisticsData} />}

        {isFetchingContractors ? (
          <CenteredLoaderContainer>
            <LoaderSpinner />
            <LoaderText>Загрузка данных...</LoaderText>
          </CenteredLoaderContainer>
        ) : (
          <EstimateTable contractors={contractorsData.contractors} />
        )}
      </ContentContainer>
    </PageContainer>
  )
}
